import { call, put, takeEvery, takeLatest } from "redux-saga/effects"
import * as api from "./api"

// Events Redux States
import {
  GET_PLAYERS,
  GET_PLAYERS_LIST,
  GET_PLAYER_INFO,
  CHANGE_PLAYERS_FILTERS,
  GET_PLAYER_TRANSACTIONS,
  GET_PLAYER_WALLET_TRANSACTIONS,
  GET_PLAYER_MEMBERSHIP_LIST,
  GET_PLAYER_ACTIVITY,
  GET_LEVEL_CHANGE,
  GET_PLAYER_ENTRIES,
  GET_PLAYER_HISTORY_ENTRIES, 
  GET_PLAYERS_LOCATIONS,
  GET_PLAYER_REFERRAL_REWARDS,
  GET_PLAYER_FIRST_TASK_REWARDS,
} from "./actionTypes"

import {
  apiError,
  getPlayersSuccess,
  getPlayersFailure,
  getPlayerInfoSuccess,
  getPlayerInfoFailure,
  changePlayersFilters,
  getPlayerTransactionSuccess,
  getPlayerWalletTransactionSuccess,
  getMembershipListSuccess,
  getPlayerActivitySuccess,
  getLevelChangeSuccess,
  getPlayersListSuccess,
  getPlayerEntriesSuccess,
  getPlayerHistoryEntriesSuccess, 
  getPlayersLocationsSuccess,
  getPlayersLocationsFailure,
  getPlayerReferralRewardsSuccess,
  getPlayerFirstTaskRewardsSuccess,
} from "./actions"

function* getPlayers({ filters, options, payload: [] }) {
  try {
    const { data } = yield call(api.getPlayers, filters, options)
    yield put(getPlayersSuccess(data))
  } catch (error) {
    yield put(getPlayersFailure(error))
  }
}

function* getPlayersLocations({ filters, payload: [] }) {
  try {
    const { data } = yield call(api.getPlayersLocations, filters)
    yield put(getPlayersLocationsSuccess(data))
  } catch (error) {
    yield put(getPlayersLocationsFailure(error))
  }
}

function* getPlayerEntries({ id, playerEntriesOptions }) {
  try {
    const { data } = yield call(
      api.getPlayerEntriesapi,
      id,
      playerEntriesOptions
    )
    yield put(
      getPlayerEntriesSuccess(data, {
        userId: id,
        options: playerEntriesOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerHistoryEntries({ id, playerHistoryEntriesOptions }) {
  try {
    const { data } = yield call(
      api.getPlayerHistoryEntriesapi,
      id,
      playerHistoryEntriesOptions
    )
    yield put(
      getPlayerHistoryEntriesSuccess(data, {
        userId: id,
        options: playerHistoryEntriesOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerTransaction({ id, transactionsOptions }) {
  try {
    const { data } = yield call(
      api.getPlayerTransactions,
      id,
      transactionsOptions
    )
    yield put(
      getPlayerTransactionSuccess(data, {
        userId: id,
        options: transactionsOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerReferralRewards({ id }) {
  try {
    const { data } = yield call(
      api.getPlayerTransactions,
      id,
      {
        type: "referralReward",
        limit: 1000,
      }
    )
    yield put(
      getPlayerReferralRewardsSuccess(data, {
        userId: id,
        options: {
          type: "referralReward",
          limit: 1000,
        },
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerFirstTaskRewards({ id }) {
  try {
    const { data } = yield call(
      api.getPlayerTransactions,
      id,
      {
        type: "firstTaskCompletion",
        limit: 1000,
      }
    )
    yield put(
      getPlayerFirstTaskRewardsSuccess(data, {
        userId: id,
        options: {
          type: "firstTaskCompletion",
          limit: 1000,
        },
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerWalletTransaction({ id, options }) {
  try {
    const { data } = yield call(api.getPlayerWalletTransactions, id, options)
    yield put(
      getPlayerWalletTransactionSuccess(data, {
        userId: id,
        options,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getMembershipList({ id, membershipOptions }) {
  try {
    const { data } = yield call(api.getMembershipList, id, membershipOptions)
    yield put(
      getMembershipListSuccess(data, {
        userId: id,
        options: membershipOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerActivity({ id, playerActivityOptions }) {
  try {
    const { data } = yield call(
      api.getPlayerActivity,
      id,
      playerActivityOptions
    )
    yield put(
      getPlayerActivitySuccess(data, {
        accountId: id,
        options: playerActivityOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getLevelChange({ id, levelChangeOptions }) {
  try {
    const { data } = yield call(api.getLevelChange, id, levelChangeOptions)
    yield put(
      getLevelChangeSuccess(data, {
        accountId: id,
        options: levelChangeOptions,
      })
    )
  } catch (error) {
    yield put(apiError(error))
  }
}

function* getPlayerInfo({ id }) {
  try {
    const { data } = yield call(api.getPlayerInfo, id)
    if (!data?.id || String(data.id) !== String(id)) {
      throw new Error("Invalid player profile response")
    }
    yield put(getPlayerInfoSuccess(data, id))
  } catch (error) {
    yield put(getPlayerInfoFailure(error, id))
  }
}

function* getPlayersList({ filters, options, payload: [] }) {
  try {
    const { data } = yield call(api.getPlayersList, filters, options)
    yield put(getPlayersListSuccess(data))
  } catch (error) {
    yield put(apiError(error))
  }
}

function* eventsSaga() {
  yield takeLatest(GET_PLAYERS, getPlayers)
  yield takeLatest(GET_PLAYERS_LOCATIONS, getPlayersLocations)
  yield takeLatest(GET_PLAYER_ENTRIES, getPlayerEntries)
  yield takeLatest(GET_PLAYER_HISTORY_ENTRIES, getPlayerHistoryEntries)
  yield takeLatest(GET_PLAYERS_LIST, getPlayersList)
  yield takeLatest(GET_PLAYER_TRANSACTIONS, getPlayerTransaction)
  yield takeLatest(GET_PLAYER_REFERRAL_REWARDS, getPlayerReferralRewards)
  yield takeLatest(GET_PLAYER_FIRST_TASK_REWARDS, getPlayerFirstTaskRewards)
  yield takeLatest(GET_PLAYER_WALLET_TRANSACTIONS, getPlayerWalletTransaction)
  yield takeLatest(GET_PLAYER_MEMBERSHIP_LIST, getMembershipList)
  yield takeLatest(GET_PLAYER_ACTIVITY, getPlayerActivity)
  yield takeLatest(GET_LEVEL_CHANGE, getLevelChange)
  yield takeLatest(GET_PLAYER_INFO, getPlayerInfo)
  yield takeEvery(CHANGE_PLAYERS_FILTERS, changePlayersFilters)
}

export default eventsSaga
